import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { QueryKey } from '../../../../constants/index';
import { HttpException } from '../../../../exceptions/index';
import { useAppMutation, useLocale, VaultHooks } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import {
  fileWithStorageDisplayName,
  resolveStorageFileName,
} from '../../../../utils/file-upload-duplicate-names.util';
import { VAULT_FILE_INVALIDATION_KEYS } from '../../constants/spaces-query.const';
import { chunksOf } from '../../utils/chunks.util';
import {
  VAULT_MAX_FILE_SIZE,
  VAULT_PARALLEL_UPLOADS,
  VAULT_UPLOAD_BATCH_SIZE,
  VAULT_UPLOAD_MIME_TYPES,
} from '../constants/vault-upload.const';

type UploadVariables = {
  // The names the folder already holds, as the Vault stores them; a match is refused up front.
  existingNames: string[];
  files: File[];
  folderId: string;
};

// The Vault folder the drop is going into and how many of the batch in flight are not listed yet.
export type VaultUploadInFlight = { count: number; folderId: string };

/**
 * Uploads into a Vault folder through the Vault's own routes — a presign, the bytes straight to
 * storage, then one call that records the files — a batch at a time, so a drop of any size gets
 * fresh URLs for each batch and a failure costs that batch alone. Shown as the browser shows a
 * workspace upload: placeholder rows in the folder's table and a reason for each file it leaves out.
 */
export const useUploadVaultFiles = () => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const queryClient = useQueryClient();
  const [inFlight, setInFlight] = useState<null | VaultUploadInFlight>(null);
  const { presignUploads } = VaultHooks.usePresignVaultUploads();
  const { createAsync } = VaultHooks.useCreateFile();

  const reject = (file: File, reason: string) =>
    toast.error(messagesLocaleFn('FILE_UPLOAD_REJECTED', { name: file.name, reason }));

  const fail = (file: File) =>
    toast.error(messagesLocaleFn('FILE_UPLOAD_FAILED', { name: file.name }));

  // The checks the Vault's upload makes before it sends anything: type, size, and a name the folder
  // or the drop already has.
  const acceptable = (files: File[], existingNames: string[]): File[] => {
    const taken = new Set(existingNames.map((name) => resolveStorageFileName(name)));
    const accepted: File[] = [];

    files.forEach((file) => {
      const storageName = resolveStorageFileName(file.name);

      if (!VAULT_UPLOAD_MIME_TYPES.includes(file.type)) {
        reject(file, messagesLocale['FILE_FORMAT_NOT_SUPPORTED']);
      } else if (file.size > VAULT_MAX_FILE_SIZE) {
        reject(file, messagesLocale['FILE_SIZE_TOO_LARGE']);
      } else if (taken.has(storageName)) {
        reject(file, messagesLocale['FILE_NAME_ALREADY_EXISTS']);
      } else {
        taken.add(storageName);
        accepted.push(file);
      }
    });

    return accepted;
  };

  // One batch through: presigned, put to storage a few at a time, recorded, then listed. Returns how
  // many of its files made it in.
  const uploadBatch = async (batch: File[], folderId: string): Promise<number> => {
    let tickets: VaultHooks.PresignedUpload[];

    try {
      tickets = await presignUploads(batch.map(({ name, size, type }) => ({ name, size, type })));
    } catch (error) {
      toast.error(
        error instanceof HttpException
          ? error.message
          : messagesLocale['UNEXPECTED_ERROR_OCCURRED'],
      );

      return 0;
    }

    const uploaded: { link: string; name: string; size: number }[] = [];
    let cursor = 0;

    // A bounded pool: enough to fill the uplink, never one connection per file.
    const worker = async () => {
      while (cursor < batch.length) {
        const index = cursor;
        cursor += 1;
        const file = batch[index];
        const { link, uploadUrl } = tickets[index];

        try {
          await ApiService.uploadFileToPresignedUrl().callback(uploadUrl, file);
          // Recorded under the name storage gave it, as the Vault records its uploads.
          uploaded.push({
            link,
            name: fileWithStorageDisplayName(file, link).name,
            size: file.size,
          });
        } catch {
          fail(file);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(VAULT_PARALLEL_UPLOADS, batch.length) }, worker),
    );

    if (!uploaded.length) return 0;

    // One call records the batch; its refusal (a name taken meanwhile, a folder that takes no
    // uploads) is told by the Vault's own mutation.
    try {
      await createAsync({ companyId, data: { files: uploaded, folderId } });
    } catch {
      return 0;
    }

    // Awaited, so the batch's placeholders give way only once its files are in the table.
    await Promise.all(
      VAULT_FILE_INVALIDATION_KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
    );

    return uploaded.length;
  };

  const { isPending, mutate: uploadFiles } = useAppMutation<number, HttpException, UploadVariables>(
    [QueryKey.UPLOAD_VAULT_FILES, companyId],
    {
      // Each file reports its own outcome, so one bad file never hides the others.
      defaultErrorHandling: false,
      mutationFn: async ({ existingNames, files, folderId }) => {
        const accepted = acceptable(files, existingNames);
        let uploaded = 0;

        try {
          for (const batch of chunksOf(accepted, VAULT_UPLOAD_BATCH_SIZE)) {
            setInFlight({ count: batch.length, folderId });
            uploaded += await uploadBatch(batch, folderId);
          }
        } finally {
          setInFlight(null);
        }

        return uploaded;
      },
      onSuccess: (uploaded) => {
        if (uploaded) toast.success(messagesLocale['FILES_UPLOADED']);
      },
    },
  );

  return { inFlight, isUploading: isPending, uploadFiles };
};
